import React, { useState } from "react";
import { Send, CheckCircle, AlertCircle, Loader2 } from "lucide-react";

export default function ContactForm({ successCallback, variant = "white", layout = "default" }) {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    description: "",
    website_url: "", // Honeypot field
  });

  const [status, setStatus] = useState("idle"); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMessage("");

    // Basic validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim() || !formData.description.trim()) {
      setStatus("error");
      setErrorMessage("Venligst udfyld alle påkrævede felter.");
      return;
    }

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.status === "ok") {
        setStatus("success");
        setFormData({
          name: "",
          phone: "",
          email: "",
          description: "",
          website_url: "",
        });
        if (successCallback) successCallback();
      } else {
        setStatus("error");
        setErrorMessage(data.message || "Der skete en fejl under afsendelsen. Prøv venligst igen.");
      }
    } catch (err) {
      setStatus("error");
      setErrorMessage("Netværksfejl. Kontroller din internetforbindelse og prøv igen.");
    }
  };

  const isGlass = variant === "glass";
  const isSidebar = layout === "sidebar";

  return (
    <div
      className={`transition-all duration-300 ${
        isGlass
          ? "bg-slate-900/45 backdrop-blur-md p-0 rounded-md shadow-2xl"
          : isSidebar
          ? "bg-white p-5 sm:p-6 rounded-md shadow-lg border border-slate-100"
          : "bg-white p-6 sm:p-10 rounded-md shadow-xl border border-slate-100"
      }`}
    >
      {status === "success" ? (
        <div className="text-center py-8 animate-in fade-in zoom-in-95 duration-300">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 mb-6">
            <CheckCircle className="h-10 w-10" />
          </div>
          <h3 className={`text-2xl font-bold mb-3 ${isGlass ? "text-white" : "text-slate-900"}`}>
            Tak for din besked!
          </h3>
          <p className={`max-w-md mx-auto leading-relaxed ${isGlass ? "text-slate-300" : "text-slate-600"}`}>
            Jeg har modtaget din forespørgsel og vender tilbage til dig med et uforpligtende tilbud inden for 24 timer.
          </p>
          <button
            onClick={() => setStatus("idle")}
            className="mt-8 text-sky-400 font-bold hover:text-sky-300 transition-colors uppercase tracking-wider text-sm"
          >
            Send en ny besked &rarr;
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Honeypot field - absolutely hidden from humans */}
          <div className="sr-only absolute -top-[9999px] -left-[9999px]" aria-hidden="true">
            <label htmlFor="website_url">Hjemmeside URL (Udfyld IKKE)</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          <div className={`grid grid-cols-1 ${isSidebar ? "" : "sm:grid-cols-2"} gap-6`}>
            <div>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="Navn *"
                className={`w-full px-4 py-3 rounded-md transition-all outline-none text-sm ${
                  isGlass
                    ? "bg-white/10 border border-white/20 text-white placeholder-white/40 focus:bg-white/15 focus:ring-2 focus:ring-sky-500"
                    : "border border-slate-200 text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                }`}
              />
            </div>

            <div>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="Telefonnummer *"
                className={`w-full px-4 py-3 rounded-md transition-all outline-none text-sm ${
                  isGlass
                    ? "bg-white/10 border border-white/20 text-white placeholder-white/40 focus:bg-white/15 focus:ring-2 focus:ring-sky-500"
                    : "border border-slate-200 text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                }`}
              />
            </div>
          </div>

          <div>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="E-mailadresse *"
              className={`w-full px-4 py-3 rounded-md transition-all outline-none text-sm ${
                isGlass
                  ? "bg-white/10 border border-white/20 text-white placeholder-white/40 focus:bg-white/15 focus:ring-2 focus:ring-sky-500"
                  : "border border-slate-200 text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
              }`}
            />
          </div>

          <div>
            <textarea
              id="description"
              name="description"
              required
              rows={isSidebar ? "3" : "4"}
              value={formData.description}
              onChange={handleChange}
              placeholder="Beskrivelse af opgaven * (f.eks. antal vinduer, solceller, tagrens osv.)"
              className={`w-full px-4 py-3 rounded-md transition-all outline-none text-sm resize-none ${
                isGlass
                  ? "bg-white/10 border border-white/20 text-white placeholder-white/40 focus:bg-white/15 focus:ring-2 focus:ring-sky-500"
                  : "border border-slate-200 text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
              }`}
            ></textarea>
          </div>

          {status === "error" && (
            <div className="flex items-center gap-3 bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-md text-sm">
              <AlertCircle className="h-5 w-5 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={status === "loading"}
            className="w-full bg-sky-500 hover:bg-sky-600 disabled:bg-sky-400 text-white py-3 px-4 sm:py-4 sm:px-6 rounded-md font-bold uppercase tracking-wider text-xs sm:text-sm transition-all shadow-sm flex items-center justify-center gap-2 cursor-pointer"
          >
            {status === "loading" ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Sender forespørgsel...</span>
              </>
            ) : (
              <>
                <Send className="h-5 w-5" />
                <span>Få et uforpligtende tilbud</span>
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
